/**
 * Iter 39 / Slice 1 — Technology Vector display helpers.
 *
 * Pure helpers — no React, no fetch — so they can be unit-tested
 * with the existing source-pin .mjs harness AND used by both
 * `TrackerTechnologyDashboard` and any future surface that renders
 * the same chips (e.g. Pipeline integration in a later slice).
 *
 * The role colour palette intentionally mirrors the
 * retention-risk / client-leakage framing from the brief —
 * Migration Candidate and Renewal Risk read as warning-tier;
 * Monitor Only reads as neutral.
 */

/**
 * Signal-level chip metadata. The label is short — operators scan
 * the priority cell first, then the chip beneath it.
 * @param {string} level - one of weak_context, pressure,
 *                         active_evaluation, active_migration
 */
export function signalLevelChip(level) {
  switch (level) {
    case 'active_migration':
      return { label: 'ACTIVE MIGRATION', color: '#ff6b6b' };
    case 'active_evaluation':
      return { label: 'ACTIVE EVALUATION', color: '#ffa94d' };
    case 'pressure':
      return { label: 'PRESSURE', color: '#ffd43b' };
    case 'weak_context':
    default:
      return { label: 'BACKGROUND', color: '#74c0fc' };
  }
}

/**
 * Priority bucket → background/foreground pair. Buckets follow the
 * scoring engine's signal-level bands (0-30 weak, 35-60 pressure,
 * 60-80 active eval, 70-100 active migration) so the colour is a
 * direct visual confirmation of the underlying level cap.
 */
export function priorityBucket(priority) {
  const p = Number(priority) || 0;
  if (p >= 70) return { bg: '#ff6b6b', fg: '#fff', label: 'high' };
  if (p >= 60) return { bg: '#ffa94d', fg: '#000', label: 'elevated' };
  if (p >= 35) return { bg: '#ffd43b', fg: '#000', label: 'pressure' };
  return { bg: '#dbe4f0', fg: '#1a2330', label: 'background' };
}

/**
 * Role pill style. The retention-risk roles (Renewal Risk, Support
 * At-Risk) and the high-intent roles (Migration Candidate,
 * Alternative Platform Evaluator) get the strongest visual weight;
 * Monitor Only is intentionally muted so weak-context rows recede
 * into the background of the table.
 */
export function rolePillStyle(role) {
  const base = {
    display: 'inline-block', padding: '3px 8px', borderRadius: 999,
    fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
  };
  switch (role) {
    case 'Migration Candidate':
      return { ...base, background: 'rgba(255,107,107,0.18)', color: '#ff6b6b' };
    case 'Renewal Risk':
      return { ...base, background: 'rgba(255,212,59,0.20)', color: '#caa800' };
    case 'Support At-Risk':
      return { ...base, background: 'rgba(255,169,77,0.20)', color: '#ff9500' };
    case 'Alternative Platform Evaluator':
      return { ...base, background: 'rgba(116,192,252,0.18)', color: '#4ea1ff' };
    case 'Modernisation Candidate':
      return { ...base, background: 'rgba(140,233,154,0.18)', color: '#3bb771' };
    case 'Cloud Shift Candidate':
      return { ...base, background: 'rgba(116,192,252,0.18)', color: '#74c0fc' };
    case 'Monitor Only':
    default:
      return { ...base, background: 'rgba(150,160,175,0.18)', color: '#8b95a3' };
  }
}

/**
 * Compose a single human-readable summary used by accessibility
 * surfaces (screen reader cell label) and CSV exports. Keeps the
 * brief's 6 visible output columns in one line.
 */
export function rowSummary(target) {
  if (!target) return '';
  const role = target.role || 'Monitor Only';
  return `${target.priority || 0} · ${role} · ${target.signal_type || ''} — ${target.suggested_angle || ''}`;
}


/**
 * Stable, compact evidence provenance for the Technology results table.
 * Dates stay in ISO day form so the server-authoritative source timestamp is
 * not shifted by a browser locale or timezone.
 */
export function evidenceProvenance(target) {
  if (!target) return '';
  const providerNames = {
    brave: 'Brave',
    duckduckgo: 'DuckDuckGo',
    google: 'Google',
    bing: 'Bing',
    newsapi: 'NewsAPI',
  };
  const sourceNames = {
    news_article: 'News article',
    web: 'Web result',
  };
  const rawProvider = String(target.provider_name || '').trim();
  const rawSource = String(target.source || '').trim();
  const rawDate = target.source_published_at;
  const date = typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate)
    ? rawDate.slice(0, 10)
    : '';
  return [
    providerNames[rawProvider.toLowerCase()] || rawProvider,
    sourceNames[rawSource.toLowerCase()] || rawSource,
    date,
  ].filter(Boolean).join(' · ');
}


/**
 * Iter 39 Slice 1b — Result-type chip metadata.
 *
 * Maps the backend `result_type` field (one of 6 values) into a UI
 * chip with a short label and a visual tier. Qualified targets get
 * a green/blue tier; Market Intelligence + Generic Noise get a muted
 * grey tier so the operator scans real targets first.
 */
export function resultTypeChip(resultType) {
  switch (resultType) {
    case 'target_organisation':
    case 'end_client_target':
      return { label: 'Target', tier: 'qualified', color: '#3bb771', bg: 'rgba(59,183,113,0.15)' };
    case 'procurement_signal':
      return { label: 'Procurement', tier: 'qualified', color: '#ff6b6b', bg: 'rgba(255,107,107,0.15)' };
    case 'end_client_hiring':
      return { label: 'End-Client Hiring', tier: 'qualified', color: '#ffa94d', bg: 'rgba(255,169,77,0.18)' };
    case 'hiring_signal':
      return { label: 'Hiring', tier: 'qualified', color: '#ffa94d', bg: 'rgba(255,169,77,0.18)' };
    case 'case_study_named_client':
      return { label: 'Named Client', tier: 'qualified', color: '#4ea1ff', bg: 'rgba(78,161,255,0.15)' };
    case 'partner_consultancy':
      return { label: 'Partner / Consultancy', tier: 'medium', color: '#9775fa', bg: 'rgba(151,117,250,0.15)' };
    case 'recruitment_undisclosed':
      return { label: 'Recruiter (undisclosed)', tier: 'unqualified', color: '#8b95a3', bg: 'rgba(139,149,163,0.12)' };
    case 'vendor_content':
      return { label: 'Vendor Content', tier: 'unqualified', color: '#8b95a3', bg: 'rgba(139,149,163,0.12)' };
    case 'market_intelligence':
      return { label: 'Market Intel', tier: 'unqualified', color: '#8b95a3', bg: 'rgba(139,149,163,0.12)' };
    case 'generic_noise':
    default:
      return { label: 'Noise', tier: 'unqualified', color: '#6c757d', bg: 'rgba(108,117,125,0.10)' };
  }
}


/**
 * Iter 39 Slice 1c — Organisation cell display.
 *
 * Returns a normalised view-model for the "Company / Organisation"
 * column. When no reliable organisation could be extracted the cell
 * renders as "Unknown / Market signal" so the operator immediately
 * sees this row is not a named target.
 */
export function organisationCell(target) {
  if (!target) return { name: 'Unknown / Market signal', isUnknown: true, confidence: 'unknown', source: 'unknown' };
  const name = (target.organisation_name || '').trim();
  const confidence = target.organisation_confidence || 'unknown';
  const source = target.organisation_source || 'unknown';
  if (!name || confidence === 'unknown') {
    return { name: 'Unknown / Market signal', isUnknown: true, confidence, source };
  }
  return { name, isUnknown: false, confidence, source };
}


/**
 * Confidence chip — small pill rendered beneath the organisation
 * name. Colour-tracks the confidence band so the operator can spot
 * a low-confidence inference at a glance.
 */
export function confidenceChip(confidence) {
  switch (confidence) {
    case 'high':
      return { label: 'high confidence', color: '#3bb771', bg: 'rgba(59,183,113,0.14)' };
    case 'medium':
      return { label: 'medium confidence', color: '#ffa94d', bg: 'rgba(255,169,77,0.16)' };
    case 'low':
      return { label: 'low confidence', color: '#caa800', bg: 'rgba(255,212,59,0.16)' };
    case 'unknown':
    default:
      return { label: 'no org match', color: '#8b95a3', bg: 'rgba(139,149,163,0.12)' };
  }
}


/**
 * Friendly label for the `organisation_source` taxonomy. Optional —
 * shown on hover/title or as a tertiary line under the org name.
 */
export function organisationSourceLabel(source) {
  switch (source) {
    case 'job_employer':         return 'from job advert';
    case 'procurement_buyer':    return 'from procurement notice';
    case 'case_study_client':    return 'from case study';
    case 'article_named_entity': return 'from news article';
    case 'domain_inferred':      return 'from domain';
    case 'unknown':
    default:                     return '';
  }
}


/**
 * Iter 39 Slice 1c — Discovery-quality summary counts.
 *
 * Pure helper that buckets a list of targets into the 4 visible
 * counts shown next to the Vector dropdown so the operator can see
 * at a glance how much of the discovery noise was real intent
 * before scrolling.
 *
 * Buckets (per slice-1c taxonomy):
 *  - namedTargets — end_client_target / target_organisation / case_study_named_client
 *  - procurement  — procurement_signal
 *  - hiring       — end_client_hiring / hiring_signal / partner_consultancy
 *  - marketIntel  — everything else (market_intelligence / vendor_content /
 *                   recruitment_undisclosed / generic_noise / influence_signal /
 *                   missing result_type)
 */
export function summariseTargets(targets) {
  const counts = { namedTargets: 0, procurement: 0, hiring: 0, marketIntel: 0, total: 0 };
  if (!Array.isArray(targets)) return counts;
  for (const t of targets) {
    counts.total += 1;
    switch (t && t.result_type) {
      case 'end_client_target':
      case 'target_organisation':
      case 'case_study_named_client':
        counts.namedTargets += 1; break;
      case 'procurement_signal':
        counts.procurement += 1; break;
      case 'end_client_hiring':
      case 'hiring_signal':
      case 'partner_consultancy':
        counts.hiring += 1; break;
      default:
        counts.marketIntel += 1; break;
    }
  }
  return counts;
}


/**
 * Iter 39 Slice 1d — Verdict-based discovery summary.
 *
 * Atech don't care how many vendor migration guides matched; they
 * care how many *organisations* the system surfaced as actionable
 * opportunities. This helper buckets by `opportunity_verdict` so
 * the badge reads:
 *   3 direct · 5 monitor · 2 partner · 12 market intel · 30 noise
 */
export function summariseVerdicts(targets) {
  const counts = {
    direct: 0, monitor: 0, partner: 0, marketIntel: 0, noise: 0, total: 0,
  };
  if (!Array.isArray(targets)) return counts;
  for (const t of targets) {
    counts.total += 1;
    switch (t && t.opportunity_verdict) {
      case 'direct_opportunity':    counts.direct      += 1; break;
      case 'monitor':               counts.monitor     += 1; break;
      case 'partner_signal':        counts.partner     += 1; break;
      case 'noise':                 counts.noise       += 1; break;
      case 'market_intelligence':
      default:                      counts.marketIntel += 1; break;
    }
  }
  return counts;
}


/**
 * Iter 39 Slice 1d — Opportunity Verdict chip metadata.
 *
 * Drives the Verdict column. Operators scan this column FIRST —
 * green Direct Opportunities float; market intel / noise are muted.
 */
export function verdictChip(verdict) {
  switch (verdict) {
    case 'direct_opportunity':
      return { label: 'Direct Opportunity', color: '#3bb771', bg: 'rgba(59,183,113,0.18)', strong: true };
    case 'monitor':
      return { label: 'Monitor',             color: '#4ea1ff', bg: 'rgba(78,161,255,0.16)', strong: true };
    case 'partner_signal':
      return { label: 'Partner Signal',      color: '#9775fa', bg: 'rgba(151,117,250,0.16)', strong: false };
    case 'noise':
      return { label: 'Noise',               color: '#6c757d', bg: 'rgba(108,117,125,0.10)', strong: false };
    case 'market_intelligence':
    default:
      // Legacy rows without an opportunity_verdict default to
      // Market Intelligence — matches summariseVerdicts' bucket.
      return { label: 'Market Intelligence', color: '#8b95a3', bg: 'rgba(139,149,163,0.12)', strong: false };
  }
}


/**
 * Row dim style — Slice 1d. Direct Opportunities + Monitor are
 * full-strength; Partner Signal is slightly dimmed; Market Intel /
 * Noise are heavily dimmed so the operator's eye lands on the
 * actionable rows.
 */
export function rowStyleForVerdict(verdict) {
  switch (verdict) {
    case 'direct_opportunity':
    case 'monitor':
      return {};
    case 'partner_signal':
      return { opacity: 0.85 };
    case 'market_intelligence':
      return { opacity: 0.7 };
    case 'noise':
      return { opacity: 0.55 };
    default:
      return {};
  }
}


/**
 * Row dim class — applied to unqualified rows so the operator's eye
 * tracks qualified targets first. The class is opacity-based so the
 * row is still readable, just visually de-emphasised.
 */
export function rowStyleForQualification(target) {
  if (!target) return {};
  // `qualified_target` is the boolean source of truth from the API;
  // fall back to inferring from `result_type` when older payloads
  // (pre-Slice-1b) are still in the table.
  const qualified = typeof target.qualified_target === 'boolean'
    ? target.qualified_target
    : !['market_intelligence', 'generic_noise', 'vendor_content', 'recruitment_undisclosed'].includes(target.result_type);
  return qualified ? {} : { opacity: 0.72 };
}
